// [ai] The chrome every route shares, mounted ONCE (docs/ARCHITECTURE.md, layer 2): the fixed bar ([☰ · Astranote] in a left
// slot as wide as the rail · crumb at the content column · the player host · page actions · ⚙), the sidebar (rail ≥ 1100 px /
// drawer below, from the shell store), the page (<Outlet/>), the dock host the Astra pill mounts in (chat/ChatUI.tsx; `--at-dock-b` is its reserve). Layout state sits
// on this root (`data-side`, `--at-side-w`); the ONE :root variable JavaScript sets is `--at-bar-h`, the bar's measured height.
import { useEffect, useLayoutEffect, useMemo, useRef, type CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation } from "react-router";
import { connectEvents } from "../api/events";
import { useAtlasQuery } from "../api/query";
import { KEYS, RAIL_PX, useShell, useShellStore } from "../stores/shell";
import { chromeSignature } from "./chrome";
import { GearIcon, MenuIcon } from "./icons";
import { routeLabel, routePattern } from "./routes";
import { installSeam } from "./seam";
import { usePlayerCtl } from "../player/context";
import { PlayerLayer } from "../player/PlayerLayer";
import { Resume } from "../player/Resume";
import { TopPlayer } from "../player/TopPlayer";
import { usePlayer } from "../stores/player";
import { Grip } from "./Grip";
import { Tree, activeHref, type TreeData } from "./Tree";
import { ImportUI, SideImport, UploadPill, useImporter } from "../imports/ImportUI";
import { useNavigate } from "react-router";
import { isAppHref } from "./links";
import { AstraDock, useAstra } from "../chat/ChatUI";
import { useWidgetsCtl } from "../widgets/widgets";

const editable = (t: EventTarget | null) => t instanceof Element && !!t.closest("input, textarea, select, [contenteditable]");

export function AppShell() {
  const store = useShellStore(); const client = useQueryClient(); const player = usePlayerCtl(); const importer = useImporter(); const astra = useAstra(); const widgets = useWidgetsCtl(); const navigate = useNavigate();
  const playerOpen = usePlayer(s => !!s.now.item || s.now.noteOnly); const playing = usePlayer(s => s.now.playing);
  const title = useShell(s => s.title); const side = useShell(s => s.side); const setSide = useShell(s => s.setSide); const toggleSide = useShell(s => s.toggleSide); const dockB = useShell(s => s.dockB);
  const { pathname, search } = useLocation(); const loc = useRef({ pathname }); loc.current = { pathname };
  const tree = useAtlasQuery<TreeData>("tree");
  const crumb = useMemo(() => {   // the tree's title for the location, the route's label otherwise, nothing on home
    if (pathname === "/") return "";
    const items = tree.data?.sections.flatMap(s => s.items) ?? []; const hit = activeHref(items, pathname, search);
    return items.find(i => i.href === hit)?.title || title || routeLabel(pathname);
  }, [tree.data, pathname, search, title]);
  useEffect(() => { store.getState().setCrumb(crumb); document.title = crumb ? `${crumb} · Astranote` : "Astranote"; }, [store, crumb]);
  // [ai] Every same-origin link on the page — a rendered document's, a card's, a legacy frame's — is a client navigation: the shell
  // never reloads for one of its own routes. Files the server serves (/raw, /api, /imports/*/file) and modified clicks pass through.
  useEffect(() => {
    const on = (e: MouseEvent) => { if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; const a = (e.target as Element).closest<HTMLAnchorElement>("a[href]"); if (!a || a.target || a.hasAttribute("download")) return; const to = isAppHref(a.getAttribute("href")); if (!to) return; e.preventDefault(); void navigate(to); };
    document.addEventListener("click", on); return () => document.removeEventListener("click", on);
  }, [navigate]);

  // the bar's measured height pads the page (two rows on a phone; the player rows when open)
  const bar = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = bar.current; if (!el) return;
    const measure = () => { const h = Math.round(el.getBoundingClientRect().height); if (h > 0) { document.documentElement.style.setProperty("--at-bar-h", h + "px"); store.getState().setBarH(h); } };
    measure(); const ro = new ResizeObserver(measure); ro.observe(el); return () => ro.disconnect();
  }, [store]);
  // rail ↔ drawer when the viewport crosses the breakpoint
  useEffect(() => { const mq = matchMedia(`(min-width: ${RAIL_PX}px)`); const on = () => store.getState().syncViewport(); mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }, [store]);
  // keys the shell owns: V toggles the sidebar, Esc closes a drawer; the player answered first (capture) when a key was its own
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.defaultPrevented || editable(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      const s = store.getState().side;
      if (e.key === "Escape" && s.open && s.mode === "drawer") { e.preventDefault(); setSide(false); }
      else if (e.key === "v" || e.key === "V") { e.preventDefault(); toggleSide(); }
    };
    document.addEventListener("keydown", on); return () => document.removeEventListener("keydown", on);
  }, [store, setSide, toggleSide]);
  // server events → the query cache; the connection state → the store
  useEffect(() => connectEvents(client, { onState: st => store.getState().setSse({ connected: st.connected }), onEvent: () => store.getState().setSse({ event: true }) }), [client, store]);
  useEffect(() => astra.bind(), [astra]);
  useEffect(() => { installSeam(store, player, importer, astra, widgets, { pathname: () => loc.current.pathname, route: () => routePattern(loc.current.pathname), chrome: () => chromeSignature() }); }, [store, player, importer, astra, widgets]);
  // the rail keeps its scroll across navigations (the old sessionStorage key)
  const aside = useRef<HTMLElement>(null);
  useEffect(() => { const el = aside.current; if (!el) return; try { el.scrollTop = +(sessionStorage.getItem(KEYS.scroll) || 0); } catch { /* private mode */ } const on = () => { try { sessionStorage.setItem(KEYS.scroll, String(Math.round(el.scrollTop))); } catch { /* private mode */ } }; el.addEventListener("scroll", on, { passive: true }); return () => el.removeEventListener("scroll", on); }, [side.open, tree.data]);   // after the tree rendered: before that the aside has nothing to scroll and clamps to 0

  const drawer = side.mode === "drawer";
  return (
    <div className="at-shell" data-side={side.open ? side.mode : "closed"} data-drag={side.dragging || undefined} data-player={playerOpen || undefined} data-playing={playing || undefined} style={{ "--at-side-w": `${side.w}px`, "--at-dock-b": `${dockB}px` } as CSSProperties}>
      <header className="at-bar" ref={bar}>
        <div className="at-bar-in">
          <div className="at-bar-l">
            <button type="button" className="at-side-btn" id="side-btn" aria-controls={side.open ? "side" : undefined} aria-expanded={side.open} title="Navigation  V" aria-label="Navigation" onClick={toggleSide}><MenuIcon /></button>
            <Link className="at-brand" to="/">Astranote</Link>
          </div>
          <span className="at-crumb">{crumb}</span>
          <div className="at-player-host"><TopPlayer /></div>
          <span className="at-sp" />
          <Resume />
          <UploadPill />
          <Link className={"at-gear" + (pathname === "/settings" ? " on" : "")} to="/settings" title="Settings" aria-label="Settings"><GearIcon /></Link>
        </div>
      </header>
      {side.open && drawer ? <div className="at-side-bg" onClick={() => setSide(false)} /> : null}
      {side.open ? (
        <aside className="at-side" id="side" aria-label="Navigation" ref={aside}>
          {drawer ? <div className="at-side-head"><Link className="at-side-brand" to="/">Astranote</Link><button type="button" className="at-btn at-side-x" aria-label="Close" onClick={() => setSide(false)}>✕</button></div> : null}
          <Tree />
          <SideImport />
        </aside>
      ) : null}
      {side.open && !drawer ? <Grip /> : null}
      <main id="main" className="at-main"><Outlet /></main>
      <div className="at-dock" id="dock"><AstraDock /></div>
      <PlayerLayer />
      <ImportUI />
    </div>
  );
}
